import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, TrendingUp, TrendingDown, Wallet, BarChart3, Percent, Shield, Activity, Loader2, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, Sparkles } from 'lucide-react';
import ImportDataModal from '../components/ImportDataModal';
import AIReviewDialog from '../components/AIReviewDialog';
import Navbar from '../components/Navbar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { mockPortfolios, mockCurrentPositions, mockUser } from '../mock';
import { getPortfolios, getPositions, getPortfolio } from '../services/api';
import { formatResidualRisk, getResidualRiskCardTheme } from '../utils/formatters';
import { analytics } from '../lib/analytics';
import type { Position, PositionsResponse, Portfolio } from '../types';
import { SortOption, Broker, getBrokerColors, getTickerColor } from '../types';

const CurrentPortfolio: React.FC = () => {
  const { portfolioId } = useParams<{ portfolioId: string }>();
  const navigate = useNavigate();
  
  // State for API data
  const [portfolios, setPortfolios] = useState<Portfolio[]>(mockPortfolios);
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [positionsData, setPositionsData] = useState<PositionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user] = useState(mockUser);
  
  // Session storage key for this portfolio's filters
  const storageKey = `portfolio-filters-${portfolioId}`;
  
  // Load saved filters from sessionStorage
  const getSavedFilters = () => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading saved filters:', e);
    }
    return null;
  };
  
  const savedFilters = getSavedFilters();
  
  // Filter/sort state - initialize from sessionStorage if available
  const [searchQuery, setSearchQuery] = useState<string>(savedFilters?.searchQuery || '');
  const [selectedCategory, setSelectedCategory] = useState<string>(savedFilters?.selectedCategory || 'all');
  const [selectedBroker, setSelectedBroker] = useState<string>(savedFilters?.selectedBroker || 'all');
  const [sortBy, setSortBy] = useState<SortOption>(savedFilters?.sortBy || SortOption.RISK);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(savedFilters?.sortDirection || 'desc');
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isAIReviewOpen, setIsAIReviewOpen] = useState(false);

  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    const filters = {
      searchQuery,
      selectedCategory,
      selectedBroker,
      sortBy,
      sortDirection,
    };
    sessionStorage.setItem(storageKey, JSON.stringify(filters));
  }, [searchQuery, selectedCategory, selectedBroker, sortBy, sortDirection, storageKey]);

  // Fetch data from API
  const fetchData = async () => {
    if (!portfolioId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch portfolios list
      const portfoliosResponse = await getPortfolios();
      if (portfoliosResponse.success && portfoliosResponse.data) {
        setPortfolios(portfoliosResponse.data);
      }
      
      // Fetch current portfolio details
      const portfolioResponse = await getPortfolio(portfolioId);
      if (portfolioResponse.success && portfolioResponse.data) {
        setPortfolio(portfolioResponse.data);
      } else {
        // Fallback to mock if portfolio not found
        const mockPortfolio = mockPortfolios.find(p => p.id === portfolioId);
        if (mockPortfolio) {
          setPortfolio(mockPortfolio);
        }
      }
      
      // Fetch positions
      const positionsResponse = await getPositions(portfolioId);
      if (positionsResponse.success && positionsResponse.data) {
        setPositionsData(positionsResponse.data);
      } else {
        // Fallback to mock data
        const mockData = mockCurrentPositions[portfolioId];
        if (mockData) {
          setPositionsData(mockData);
        } else {
          setError('No positions found for this portfolio');
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
      // Fallback to mock data on error
      const mockPortfolio = mockPortfolios.find(p => p.id === portfolioId);
      if (mockPortfolio) {
        setPortfolio(mockPortfolio);
      }
      const mockData = mockCurrentPositions[portfolioId || ''];
      if (mockData) {
        setPositionsData(mockData);
      }
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [portfolioId]);

  // Format duration as "Xm Yd"
  const formatDuration = (days: number) => {
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;
    return `${months}m ${remainingDays}d`;
  };

  const handleTabChange = (value: string) => {
    if (value === 'journal') {
      navigate(`/portfolio/${portfolioId}/journal`);
    }
  };

  const handleStockClick = (stock: Position) => {
    // Track position detail click
    analytics.trackPositionDetailClicked(stock.id, stock.ticker, 'portfolio_list');
    navigate(`/portfolio/${portfolioId}/stock/${stock.id}`);
  };

  // Use positionsData from API or fall back to empty
  const positions = positionsData?.positions || [];
  const summary = positionsData?.summary;

  const filteredPositions = positions.filter(position => {
    const matchesSearch = position.ticker.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || position.category === selectedCategory;
    const matchesBroker = selectedBroker === 'all' || position.broker === selectedBroker;
    return matchesSearch && matchesCategory && matchesBroker;
  });

  // Calculate filtered summary based on filtered positions
  const filteredSummary = useMemo(() => {
    const positions = filteredPositions || [];
    
    const totalInvestment = positions.reduce((sum, p) => sum + (p.investedAmount || 0), 0);
    const currentValue = positions.reduce((sum, p) => sum + (p.currentValue || 0), 0);
    const unrealizedPnL = positions.reduce((sum, p) => sum + (p.unrealizedPnL || 0), 0);
    const residualRisk = positions.reduce((sum, p) => sum + (p.residualRisk || 0), 0);
    
    // Calculate daily change from positions that have daily change data
    const positionsWithDailyChange = positions.filter(p => p.dailyChange !== null && p.dailyChange !== undefined);
    const dailyChangePercent = positionsWithDailyChange.length > 0
      ? positionsWithDailyChange.reduce((sum, p) => {
          // Weight by current value for accurate portfolio daily change
          return sum + ((p.dailyChange || 0) * (p.currentValue || 0));
        }, 0) / (positionsWithDailyChange.reduce((sum, p) => sum + (p.currentValue || 0), 0) || 1)
      : 0;
    
    // Calculate absolute daily change
    const dailyChange = positionsWithDailyChange.reduce((sum, p) => {
      const prevValue = (p.currentValue || 0) / (1 + (p.dailyChange || 0) / 100);
      return sum + ((p.currentValue || 0) - prevValue);
    }, 0);
    
    // Calculate total return percentage based on unrealized P&L (not currentValue - totalInvestment)
    // This is correct because unrealizedPnL is calculated per position based on cost basis of remaining shares
    const totalReturn = totalInvestment > 0 
      ? ((unrealizedPnL / totalInvestment) * 100).toFixed(2)
      : '0.00';
    
    return {
      totalInvestment,
      currentValue,
      unrealizedPnL,
      totalReturn,
      residualRisk,
      dailyChange: Math.round(dailyChange),
      dailyChangePercent: parseFloat(dailyChangePercent.toFixed(2)),
    };
  }, [filteredPositions]);

  // Sort positions based on selected criteria and direction
  const sortedPositions = useMemo(() => {
    const sorted = [...(filteredPositions || [])].sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case SortOption.SYMBOL:
          comparison = a.ticker.localeCompare(b.ticker);
          break;
        case SortOption.PNL_ABSOLUTE:
          // Sort by actual P&L value (positive first in desc, negative first in asc)
          comparison = (b.unrealizedPnL || 0) - (a.unrealizedPnL || 0);
          break;
        case SortOption.PNL_PERCENT:
          // Sort by actual P&L percentage (positive first in desc, negative first in asc)
          comparison = (b.unrealizedPnLPercent || 0) - (a.unrealizedPnLPercent || 0);
          break;
        case SortOption.PORTFOLIO_PERCENT:
          // Calculate portfolio percentage from tranche breakdown
          const aTranchePercent = (a.trancheBreakdown || []).reduce((sum, t) => sum + t.percentage, 0);
          const bTranchePercent = (b.trancheBreakdown || []).reduce((sum, t) => sum + t.percentage, 0);
          comparison = bTranchePercent - aTranchePercent;
          break;
        case SortOption.DURATION:
          comparison = b.durationDays - a.durationDays;
          break;
        case SortOption.VALUE:
          comparison = b.currentValue - a.currentValue;
          break;
        case SortOption.RISK:
          comparison = b.residualRisk - a.residualRisk;
          break;
        case SortOption.TO_SL:
          // Calculate "To SL" percentage: ((currentPrice - stoploss) / currentPrice) * 100
          // Lower values mean closer to stoploss (more urgent)
          const aToSL = (a.currentPrice !== null && a.stoploss > 0)
            ? ((a.currentPrice - a.stoploss) / a.currentPrice) * 100
            : Infinity; // Positions without stoploss or price go to bottom
          const bToSL = (b.currentPrice !== null && b.stoploss > 0)
            ? ((b.currentPrice - b.stoploss) / b.currentPrice) * 100
            : Infinity;
          comparison = aToSL - bToSL; // Lower (closer to SL) first
          break;
        case SortOption.DAILY_CHANGE:
          // Sort by daily change percentage
          // Positions without daily change data go to bottom
          const aDailyChange = a.dailyChange ?? -Infinity;
          const bDailyChange = b.dailyChange ?? -Infinity;
          comparison = bDailyChange - aDailyChange; // Higher change first
          break;
        case SortOption.WEEKLY_CHANGE:
          // Sort by weekly change percentage
          // Positions without weekly change data go to bottom
          const aWeeklyChange = a.weeklyChange ?? -Infinity;
          const bWeeklyChange = b.weeklyChange ?? -Infinity;
          comparison = bWeeklyChange - aWeeklyChange; // Higher change first
          break;
        case SortOption.MONTHLY_CHANGE:
          // Sort by monthly change percentage
          // Positions without monthly change data go to bottom
          const aMonthlyChange = a.monthlyChange ?? -Infinity;
          const bMonthlyChange = b.monthlyChange ?? -Infinity;
          comparison = bMonthlyChange - aMonthlyChange; // Higher change first
          break;
        default:
          comparison = 0;
      }
      // Apply sort direction (comparison is default desc, so flip for asc)
      return sortDirection === 'asc' ? -comparison : comparison;
    });
    return sorted;
  }, [filteredPositions, sortBy, sortDirection]);

  // Helper to get sort field label
  const getSortLabel = (option: SortOption): string => {
    switch (option) {
      case SortOption.SYMBOL: return 'Symbol';
      case SortOption.PNL_ABSOLUTE: return 'P&L (Absolute)';
      case SortOption.PNL_PERCENT: return 'P&L (%)';
      case SortOption.PORTFOLIO_PERCENT: return '% of Portfolio';
      case SortOption.DURATION: return 'Duration';
      case SortOption.VALUE: return 'Value';
      case SortOption.RISK: return 'Risk';
      case SortOption.TO_SL: return 'To SL';
      case SortOption.DAILY_CHANGE: return 'Daily Change';
      case SortOption.WEEKLY_CHANGE: return 'Weekly Change';
      case SortOption.MONTHLY_CHANGE: return 'Monthly Change';
      default: return 'P&L (Absolute)';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar
          portfolios={portfolios}
          selectedPortfolio={portfolioId}
          onPortfolioChange={(id) => navigate(`/portfolio/${id}/current`)}
          user={user}
        />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading positions...</span>
        </div>
      </div>
    );
  }

  if (!portfolio || !positionsData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar
          portfolios={portfolios}
          selectedPortfolio={portfolioId}
          onPortfolioChange={(id) => navigate(`/portfolio/${id}/current`)}
          user={user}
        />
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-gray-500">{error || 'Portfolio not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar
        portfolios={portfolios}
        selectedPortfolio={portfolioId}
        onPortfolioChange={(id) => navigate(`/portfolio/${id}/current`)}
        user={user}
      />

      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Header with Centered Tabs */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">{portfolio.name}</h1>
            <p className="text-sm text-gray-500">Last synced: Today at 2:45 PM</p>
          </div>
          
          <Tabs defaultValue="current" className="w-auto" onValueChange={handleTabChange}>
            <TabsList>
              <TabsTrigger value="current">Current</TabsTrigger>
              <TabsTrigger value="journal">Journal</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="gap-2 border-purple-600 text-purple-600 hover:bg-purple-50"
              onClick={() => {
                // Track AI review click
                analytics.trackAIReviewClicked(portfolioId || '', positions.length);
                setIsAIReviewOpen(true);
              }}
            >
              <Sparkles className="w-4 h-4" />
              Review with AI
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50"
              onClick={() => {
                analytics.trackImportModalOpened(portfolioId || '');
                setIsImportModalOpen(true);
              }}
            >
              <RefreshCw className="w-4 h-4" />
              Import Data
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap gap-4 items-center">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {(portfolio?.categories || []).map(cat => (
                <SelectItem key={cat.name} value={cat.name}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedBroker} onValueChange={setSelectedBroker}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Brokers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Brokers</SelectItem>
              {portfolio?.brokers.map((broker) => (
                <SelectItem key={broker} value={broker}>{broker}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search stocks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <Button 
            variant="outline"
            onClick={() => {
              setSelectedCategory('all');
              setSelectedBroker('all');
              setSearchQuery('');
              setSortBy(SortOption.RISK);
              setSortDirection('desc');
              sessionStorage.removeItem(storageKey);
            }}
          >
            Reset Filters
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <Wallet className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-900">₹{filteredSummary.totalInvestment.toLocaleString('en-IN')}</p>
            <p className="text-xs text-blue-700">Total Investment</p>
          </Card>

          <Card className="p-4 bg-green-50 border-green-200">
            <div className="flex items-center justify-between mb-2">
              <BarChart3 className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-green-900">₹{filteredSummary.currentValue.toLocaleString('en-IN')}</p>
            <p className="text-xs text-green-700">Current Value</p>
            <p className={`text-xs mt-1 ${filteredSummary.unrealizedPnL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {filteredSummary.unrealizedPnL >= 0 ? '+' : ''}₹{filteredSummary.unrealizedPnL.toLocaleString('en-IN')} overall
            </p>
          </Card>

          <Card className="p-4 bg-emerald-50 border-emerald-200">
            <div className="flex items-center justify-between mb-2">
              {filteredSummary.unrealizedPnL >= 0 ? (
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-600" />
              )}
            </div>
            <p className={`text-2xl font-bold ${filteredSummary.unrealizedPnL >= 0 ? 'text-emerald-900' : 'text-red-600'}`}>
              {filteredSummary.unrealizedPnL >= 0 ? '+' : ''}₹{filteredSummary.unrealizedPnL.toLocaleString('en-IN')}
            </p>
            <p className="text-xs text-emerald-700">Unrealized P&L</p>
          </Card>

          <Card className="p-4 bg-purple-50 border-purple-200">
            <div className="flex items-center justify-between mb-2">
              <Percent className="w-5 h-5 text-purple-600" />
            </div>
            <p className={`text-2xl font-bold ${parseFloat(filteredSummary.totalReturn) >= 0 ? 'text-purple-900' : 'text-red-600'}`}>
              {parseFloat(filteredSummary.totalReturn) >= 0 ? '+' : ''}{filteredSummary.totalReturn}%
            </p>
            <p className="text-xs text-purple-700">Total Return</p>
          </Card>

          <Card className={`p-4 ${getResidualRiskCardTheme(filteredSummary.residualRisk)}`}>
            <div className="flex items-center justify-between mb-2">
              <Shield className={`w-5 h-5 ${formatResidualRisk(filteredSummary.residualRisk).colorClass}`} />
            </div>
            <p className={`text-2xl font-bold ${formatResidualRisk(filteredSummary.residualRisk).colorClass}`}>
              {formatResidualRisk(filteredSummary.residualRisk).value}
            </p>
            <p className={`text-xs ${formatResidualRisk(filteredSummary.residualRisk).colorClass}`}>Residual Risk</p>
          </Card>

          <Card className="p-4 bg-cyan-50 border-cyan-200">
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-5 h-5 text-cyan-600" />
            </div>
            {filteredSummary.dailyChange !== 0 || filteredSummary.dailyChangePercent !== 0 ? (
              <>
                <p className={`text-2xl font-bold ${filteredSummary.dailyChange >= 0 ? 'text-cyan-900' : 'text-red-600'}`}>
                  {filteredSummary.dailyChange >= 0 ? '+' : ''}₹{Math.abs(filteredSummary.dailyChange).toLocaleString('en-IN')}
                </p>
                <p className="text-xs text-cyan-700">Daily Change</p>
                <p className={`text-xs mt-1 ${filteredSummary.dailyChangePercent >= 0 ? 'text-cyan-600' : 'text-red-500'}`}>
                  {filteredSummary.dailyChangePercent >= 0 ? '+' : ''}{filteredSummary.dailyChangePercent}%
                </p>
              </>
            ) : (
              <>
                <p className="text-2xl font-bold text-gray-400">N/A</p>
                <p className="text-xs text-cyan-700">Daily Change</p>
                <p className="text-xs text-gray-400 mt-1">No historical data</p>
              </>
            )}
          </Card>
        </div>

        {/* Open Positions */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Open Positions <span className="text-gray-500 text-base ml-2">({filteredPositions?.length})</span>
            </h2>
            <div className="flex items-center gap-2">
              <Select value={sortBy} onValueChange={(value) => setSortBy(value as SortOption)}>
                <SelectTrigger className="w-[180px]">
                  <ArrowUpDown className="w-4 h-4 mr-2 text-gray-500" />
                  <SelectValue placeholder="Sort by">{getSortLabel(sortBy)}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SortOption.SYMBOL}>Symbol</SelectItem>
                  <SelectItem value={SortOption.PNL_ABSOLUTE}>P&L (Absolute)</SelectItem>
                  <SelectItem value={SortOption.PNL_PERCENT}>P&L (%)</SelectItem>
                  <SelectItem value={SortOption.PORTFOLIO_PERCENT}>% of Portfolio</SelectItem>
                  <SelectItem value={SortOption.DURATION}>Duration</SelectItem>
                  <SelectItem value={SortOption.VALUE}>Value</SelectItem>
                  <SelectItem value={SortOption.RISK}>Risk</SelectItem>
                  <SelectItem value={SortOption.TO_SL}>To SL</SelectItem>
                  <SelectItem value={SortOption.DAILY_CHANGE}>Daily Change</SelectItem>
                  <SelectItem value={SortOption.WEEKLY_CHANGE}>Weekly Change</SelectItem>
                  <SelectItem value={SortOption.MONTHLY_CHANGE}>Monthly Change</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex border border-gray-200 rounded-md overflow-hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  className={`px-3 py-2 rounded-none border-r border-gray-200 ${
                    sortDirection === 'asc' 
                      ? 'bg-blue-50 text-blue-700' 
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                  onClick={() => setSortDirection('asc')}
                >
                  <ArrowUp className="w-4 h-4 mr-1" />
                  Asc
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`px-3 py-2 rounded-none ${
                    sortDirection === 'desc' 
                      ? 'bg-blue-50 text-blue-700' 
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                  onClick={() => setSortDirection('desc')}
                >
                  <ArrowDown className="w-4 h-4 mr-1" />
                  Desc
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-3">
              {sortedPositions?.map((stock) => {
                const isProfitable = stock.unrealizedPnL !== null ? stock.unrealizedPnL >= 0 : null;
                const categoryColor = portfolio.categories.find(c => c.name === stock.category)?.color || '#6B7280';
                // Use trancheBreakdown from data, or generate mock if not available
                const tranches = stock.trancheBreakdown || Array.from({ length: stock.trancheCount }, (_, i) => ({
                  id: `t${i + 1}`,
                  type: 'buy' as const,
                  percentage: parseFloat((Math.random() * 3 + 0.5).toFixed(1)),
                  quantity: 0,
                  price: 0
                }));
                
                return (
                  <Card
                    key={stock.id}
                    className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleStockClick(stock)}
                  >
                    <div className="flex items-center justify-between gap-4 mb-3">
                      <div className="flex items-center gap-4 flex-1">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-white text-sm flex-shrink-0"
                          style={{ backgroundColor: getTickerColor(stock.ticker) }}
                        >
                          {stock.ticker.substring(0, 3)}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 text-sm">{stock.ticker}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span
                              className="px-2 py-0.5 text-xs font-medium rounded"
                              style={{ backgroundColor: `${categoryColor}20`, color: categoryColor }}
                            >
                              {stock.category}
                            </span>
                            <span className="text-xs text-gray-500">•</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${getBrokerColors(stock.broker).bg} ${getBrokerColors(stock.broker).text}`}>
                              {stock.broker}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-6 text-sm">
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Quantity</p>
                          <p className="font-semibold text-gray-900">{stock.availableQuantity}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Avg Price</p>
                          <p className="font-semibold text-gray-900">₹{stock.avgBuyPrice?.toLocaleString('en-IN') || 'N/A'}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Current Price</p>
                          <p className="font-semibold text-gray-900">
                            {stock.currentPrice !== null ? `₹${stock.currentPrice.toLocaleString('en-IN')}` : 'N/A'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Stoploss</p>
                          <p className="font-semibold text-gray-900">₹{stock.stoploss?.toLocaleString('en-IN') || 'N/A'}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">To SL</p>
                          <p className="font-semibold text-gray-700">
                            {stock.currentPrice !== null && stock.stoploss > 0
                              ? `${(((stock.currentPrice - stock.stoploss) / stock.currentPrice) * 100).toFixed(1)}%`
                              : 'N/A'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Invested</p>
                          <p className="font-semibold text-gray-900">₹{stock.investedAmount.toLocaleString('en-IN')}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Current Value</p>
                          <p className="font-semibold text-gray-900">
                            {stock.currentValue !== null ? `₹${stock.currentValue.toLocaleString('en-IN')}` : 'N/A'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">% of Portfolio</p>
                          <p className="font-semibold text-indigo-600">
                            {tranches.length > 0 
                              ? `${tranches.reduce((sum, t) => sum + t.percentage, 0).toFixed(1)}%`
                              : 'N/A'}
                          </p>
                        </div>
                        <div className="text-center min-w-[120px]">
                          <p className="text-xs text-gray-500">P&L</p>
                          {stock.unrealizedPnL !== null ? (
                            <>
                              <p className={`font-semibold ${isProfitable ? 'text-green-600' : 'text-red-600'}`}>
                                {isProfitable ? '+' : ''}₹{stock.unrealizedPnL.toLocaleString('en-IN')}
                              </p>
                              <p className={`text-xs ${isProfitable ? 'text-green-600' : 'text-red-600'}`}>
                                ({isProfitable ? '+' : ''}{stock.unrealizedPnLPercent}%)
                              </p>
                            </>
                          ) : (
                            <p className="font-semibold text-gray-500">N/A</p>
                          )}
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Residual Risk</p>
                          <p className={`font-semibold ${formatResidualRisk(stock.residualRisk).colorClass}`}>
                            {formatResidualRisk(stock.residualRisk).value}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Duration</p>
                          <p className="font-semibold text-gray-900">{formatDuration(stock.durationDays)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Tranche Information & Daily/Weekly Changes - Second Line */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-xs">
                        {tranches.map((t, idx) => (
                          <span 
                            key={t.id} 
                            className={`px-1.5 py-0.5 rounded ${
                              t.type === 'buy' 
                                ? 'bg-green-50 text-green-700' 
                                : 'bg-red-50 text-red-700'
                            }`}
                          >
                            T{idx + 1} {t.type === 'buy' ? '+' : '-'}{Math.abs(t.percentage)}%
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex items-center gap-4 text-xs">
                        <div className="text-center">
                          <p className="text-gray-500">Daily</p>
                          {stock.dailyChange !== undefined && stock.dailyChange !== null ? (
                            <p className={`font-semibold ${stock.dailyChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {stock.dailyChange > 0 ? '+' : ''}{stock.dailyChange}%
                            </p>
                          ) : (
                            <p className="font-semibold text-gray-400">N/A</p>
                          )}
                        </div>
                        <div className="text-center">
                          <p className="text-gray-500">Weekly</p>
                          {stock.weeklyChange !== undefined && stock.weeklyChange !== null ? (
                            <p className={`font-semibold ${stock.weeklyChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {stock.weeklyChange > 0 ? '+' : ''}{stock.weeklyChange}%
                            </p>
                          ) : (
                            <p className="font-semibold text-gray-400">N/A</p>
                          )}
                        </div>
                        <div className="text-center">
                          <p className="text-gray-500">Monthly</p>
                          {stock.monthlyChange !== undefined && stock.monthlyChange !== null ? (
                            <p className={`font-semibold ${stock.monthlyChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {stock.monthlyChange > 0 ? '+' : ''}{stock.monthlyChange}%
                            </p>
                          ) : (
                            <p className="font-semibold text-gray-400">N/A</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
        </div>
      </div>
      
      {/* Import Data Modal */}
      <ImportDataModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        portfolioId={portfolioId!}
        portfolioBrokers={portfolio?.brokers || []}
        onImportComplete={() => {
          // Refresh data after import
          fetchData();
        }}
      />
      
      {/* AI Review Dialog */}
      <AIReviewDialog
        isOpen={isAIReviewOpen}
        onClose={() => setIsAIReviewOpen(false)}
        portfolioId={portfolioId!}
      />
    </div>
  );
};

export default CurrentPortfolio;
